// src/components/salesinvoice/SalesInvoiceView.jsx
import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Navbar from '../../components/layout/Navbar';
import { SALES_INVOICE_DETAILS_API, SALES_INVOICE_DELETE_API } from '../../config/api';
import { decryptData } from '../../utils/encrypt';

// ---- dd-MMM-YY (safe for "YYYY-MM-DD" and ISO) ----
const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const formatDMY = (value) => {
  if (!value && value !== 0) return "-";
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [y, m, d] = value.split("-").map(Number);
    const dt = new Date(y, m - 1, d);
    if (Number.isNaN(dt.getTime())) return "-";
    const day = String(dt.getDate()).padStart(2, "0");
    const mon = MONTHS[dt.getMonth()];
    const yy  = String(dt.getFullYear()).slice(-2);
    return `${day}-${mon}-${yy}`;
  }
  const dt = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(dt.getTime())) return "-";
  const day = String(dt.getDate()).padStart(2, "0");
  const mon = MONTHS[dt.getMonth()];
  const yy  = String(dt.getFullYear()).slice(-2);
  return `${day}-${mon}-${yy}`;
};

// ---- helpers ----
const safeNum = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : def;
};

function SalesInvoiceView() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const invoiceItemId = searchParams.get('id');

  const [invoiceData, setInvoiceData] = useState(null);
  const [userId, setUserId] = useState(null);
  const [orgId, setOrgId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleted, setDeleted] = useState(false); // <- NEW: keep disabled after success

  useEffect(() => {
    const stored = localStorage.getItem('suyog_user');
    if (stored) {
      const parsed = decryptData(stored);
      setUserId(parsed?.user_id || null);
    }
    const storedOrg = localStorage.getItem('suyog_selected_org');
    if (storedOrg) {
      const parsedOrg = JSON.parse(storedOrg);
      setOrgId(parsedOrg?.value || null);
    }
  }, []);

  useEffect(() => {
    if (!invoiceItemId) return;

    (async () => {
      try {
        const res = await fetch(SALES_INVOICE_DETAILS_API, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: parseInt(invoiceItemId, 10) })
        });
        const json = await res.json();
        setInvoiceData(json?.data || null);
      } catch (error) {
        console.error('Fetch error:', error);
        alert('Error fetching invoice details');
      }
    })();
  }, [invoiceItemId]);

  const closeTabOrBack = () => {
    if (window.opener && !window.opener.closed) {
      window.close();
    } else {
      navigate(-1);
    }
  };

  const handleDeleteInvoice = async () => {
    if (deleted) return; // already deleted
    if (!invoiceData?.tally_invoice_no) {
      alert('Invoice number not available.');
      return;
    }
    if (!userId) {
      alert('User not loaded.');
      return;
    }
    const ok = window.confirm(
      `Delete invoice ${invoiceData.tally_invoice_no}? This will remove the invoice and all its items/documents.`
    );
    if (!ok) return;

    try {
      setDeleting(true);
      const res = await fetch(SALES_INVOICE_DELETE_API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tally_invoice_no: invoiceData.tally_invoice_no,
          user_id: userId,
          org_id: orgId || undefined,
        }),
      });
      const json = await res.json();
      if (json?.success) {
        setDeleted(true);   // <- lock the button permanently
        setDeleting(false); // update label from "Deleting…" to "Deleted"
        alert(`Invoice ${invoiceData.tally_invoice_no} deleted.`);
        // close or go back shortly (if blocked, button remains disabled)
        setTimeout(closeTabOrBack, 200);
      } else {
        alert(`Delete failed: ${json?.error || 'Unknown error'}`);
        setDeleting(false);
      }
    } catch (e) {
      console.error('Delete error:', e);
      alert(`Server error during delete: ${e?.message || e}`);
      setDeleting(false);
    }
  };

  if (!invoiceItemId) {
    return <div className="container mt-5 text-center text-danger">Invoice ID is missing in the URL.</div>;
  }

  if (!invoiceData) {
    return <div className="container mt-5 text-center">Loading invoice details...</div>;
  }

  const items = Array.isArray(invoiceData.items) ? invoiceData.items : [];
  const documents = Array.isArray(invoiceData.documents) ? invoiceData.documents : [];
  const loadingPoints = Array.isArray(invoiceData.loading_points) ? invoiceData.loading_points : [];
  const destinations = Array.isArray(invoiceData.destinations) ? invoiceData.destinations : [];

  const transport = invoiceData.transport || {};
  const dispatch = invoiceData.dispatch || {};

  const headerTitle = invoiceData.tally_invoice_no || `#${invoiceItemId}`;

  return (
    <>
      <Navbar />
      <div className="container mt-5">
        <div className="card shadow-sm p-4">

          {/* Header + Delete */}
          <div className="d-flex align-items-start justify-content-between">
            <h4 className="mb-4 text-primary">
              Invoice Details - {headerTitle}{' '}
              <small className="text-muted">Dtd {formatDMY(invoiceData.invoice_date)}</small>
            </h4>

            <button
              className="btn btn-outline-danger"
              disabled={deleting || deleted || !invoiceData.tally_invoice_no}
              onClick={handleDeleteInvoice}
              title={
                deleted
                  ? 'Invoice already deleted'
                  : (!invoiceData.tally_invoice_no ? 'No invoice number to delete' : 'Delete Invoice')
              }
            >
              {deleted ? '✅ Deleted' : (deleting ? 'Deleting…' : '🗑️ Delete Invoice')}
            </button>
          </div>

          <div className="row g-3">
            <div className="col-md-6">
              <div className="border rounded p-3 h-100">
                <h6 className="mb-3">Customer & PO</h6>
                <div><strong>Customer:</strong> {invoiceData.customer_name || "-"}</div>
                <div><strong>PO No:</strong> {invoiceData.customer_po_no || "-"}</div>
                <div><strong>Invoice Date:</strong> {formatDMY(invoiceData.invoice_date)}</div>
                <hr />
                <div><strong>Created by:</strong> {invoiceData.created_by_name || "-"} &nbsp; <small className="text-muted">({formatDMY(invoiceData.created_on)})</small></div>
                <div><strong>Updated by:</strong> {invoiceData.updated_by_name || "-"} &nbsp; <small className="text-muted">({formatDMY(invoiceData.updated_on)})</small></div>
                <div><strong>Organization:</strong> {invoiceData.org_name || "-"}</div>
              </div>
            </div>

            {/* Transport + Dispatch */}
            <div className="col-md-6">
              <div className="border rounded p-3 h-100">
                <h6 className="mb-3">Transport & Dispatch</h6>

                <div className="mb-2"><strong>Transporter:</strong> {transport.transporter_name || "-"}</div>
                <div className="row">
                  <div className="col-sm-6">
                    <div><small className="text-muted">Contact Person:</small> {transport.transporter_contact_person || "-"}</div>
                    <div><small className="text-muted">Mobile:</small> {transport.transporter_mobile || "-"}</div>
                  </div>
                </div>

                <hr className="my-2" />

                <div className="mb-2"><strong>Vehicle Planned Date:</strong> {formatDMY(dispatch.dispatch_date)}</div>
                <div className="row">
                  <div className="col-sm-6">
                    <div><small className="text-muted">Vehicle No:</small> {dispatch.vehicle_no || "-"}</div>
                    <div><small className="text-muted">Driver:</small> {dispatch.driver_name || "-"}</div>
                  </div>
                  <div className="col-sm-6">
                    <div><small className="text-muted">Driver Mobile:</small> {dispatch.driver_mobile || "-"}</div>
                    <div><small className="text-muted">Remark:</small> {dispatch.remark || "-"}</div>
                  </div>
                </div>

                <div className="row mt-2">
                  <div className="col-sm-6">
                    <div><small className="text-muted">Diesel (Ltrs):</small> {dispatch.diesel_ltr ?? "-"}</div>
                  </div>
                  <div className="col-sm-6">
                    <div><small className="text-muted">Cash Amount:</small> {dispatch.cash_amount ?? "-"}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Destinations */}
          <h5 className="mt-4 mb-2">Destination(s)</h5>
          {destinations.length === 0 ? (
            <div className="text-muted">No destination info available.</div>
          ) : (
            <div className="table-responsive">
              <table className="table table-bordered table-sm small">
                <thead className="table-light">
                  <tr>
                    <th>#</th>
                    <th>Address</th>
                    <th>City</th>
                    <th>Contact Details</th>
                  </tr>
                </thead>
                <tbody>
                  {destinations.map((d, i) => (
                    <tr key={d.id ?? i}>
                      <td>{i + 1}</td>
                      <td>{d.destination_address || "-"}</td>
                      <td>{d.destination_city || "-"}</td>
                      <td>{d.remark || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Items (read-only) */}
          <h5 className="mt-4 mb-2">Items</h5>
          <div className="table-responsive">
            <table className="table table-bordered table-sm small">
              <thead className="table-light">
                <tr>
                  <th>#</th>
                  <th>Material</th>
                  <th>Qty</th>
                  <th>Unit</th>
                  <th>Price</th>
                  <th>GST %</th>
                  <th>Invoice No</th>
                  <th>Invoice Date</th>
                  <th>Destination (Plan)</th>
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr><td colSpan={10} className="text-center text-muted">No items</td></tr>
                ) : items.map((item, index) => {
                  const total = (safeNum(item.qty) * safeNum(item.price)).toFixed(2);
                  return (
                    <tr key={item.id}>
                      <td>{index + 1}</td>
                      <td>{item.material_name}</td>
                      <td>{item.qty}</td>
                      <td>{item.unit || "N/A"}</td>
                      <td>{item.price}</td>
                      <td>{item.gst}</td>
                      <td>{item.tally_invoice_no || '-'}</td>
                      <td>{formatDMY(item.tally_invoice_date) || '-'}</td>
                      <td>{item.destination_name || "-"}</td>
                      <td>{total}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Loading Points */}
          <h5 className="mt-4">Loading Points</h5>
          {loadingPoints.length === 0 ? (
            <div className="text-muted">-</div>
          ) : (
            <ul className="mb-3">
              {loadingPoints.map((lp, i) => (
                <li key={i}>
                  {lp.loading_point_name} (Qty: {lp.qty})
                </li>
              ))}
            </ul>
          )}

          {/* Documents */}
          <h5 className="mt-4">Documents</h5>
          {documents.length === 0 ? (
            <div className="text-muted">-</div>
          ) : (
            <ul className="mb-3">
              {documents.map((doc, i) => (
                <li key={i}>{doc.filename} ({doc.size_kb} KB)</li>
              ))}
            </ul>
          )}

          {/* Grand Total */}
          <div className="text-end mt-2">
            <strong>Grand Total: ₹ {invoiceData.total_amount}</strong>
          </div>
        </div>
      </div>
    </>
  );
}

export default SalesInvoiceView;
